import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AgentRefreshService } from './agent-refresh.service';
import { AgentApprovalField } from './approval-fields';

/** A change the person's own agent (Claude, Cursor … over MCP) proposed and that waits for them. */
export interface AgentExternalApproval {
  id: string;
  action_id?: string | null;
  risk?: string | null;
  connection?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
  preview?: {
    summary?: string;
    reason?: string;
    fields?: AgentApprovalField[];
    before?: unknown;
    ui_target?: string | null;
    ui_anchor?: string | null;
  } | null;
  /** local state after deciding */
  state?: 'pending' | 'sending' | 'executed' | 'failed' | 'rejected' | 'expired_or_unknown';
}

const OPEN_POLL_MS = 10_000;
const CLOSED_POLL_MS = 60_000;
const SETTLED_SHOWN_MS = 6_000;

/**
 * "Offene Freigaben": changes proposed over MCP by the person's own agent
 * (al-tutor `/portal-agent/approvals`). The external model cannot approve —
 * the person does it here, in the panel, with the same card as for the
 * in-house agent. Polls only while the agent is on for the organisation and
 * the tab is visible: every 10 s with the panel open, else every minute.
 */
@Injectable({ providedIn: 'root' })
export class AgentApprovalsService {
  private readonly http = inject(HttpClient);
  private readonly refresh = inject(AgentRefreshService);
  private readonly base = `${environment.tutorsBackend}/portal-agent/approvals`;

  readonly items = signal<AgentExternalApproval[]>([]);
  readonly openCount = computed(() => this.items().filter((a) => (a.state ?? 'pending') === 'pending').length);

  private timer: ReturnType<typeof setTimeout> | null = null;
  private panelOpen = false;
  private running = false;

  /** Start (or keep) polling; call with the panel's open state whenever it changes. */
  watch(enabled: boolean, panelOpen: boolean): void {
    this.panelOpen = panelOpen;
    if (!enabled) {
      this.stop();
      this.items.set([]);
      return;
    }
    if (!this.running) {
      this.running = true;
      void this.tick();
    } else if (panelOpen) {
      void this.load();
    }
  }

  stop(): void {
    this.running = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private async tick(): Promise<void> {
    if (!this.running) return;
    if (typeof document === 'undefined' || document.visibilityState === 'visible') await this.load();
    this.timer = setTimeout(() => void this.tick(), this.panelOpen ? OPEN_POLL_MS : CLOSED_POLL_MS);
  }

  async load(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.get<{ data: AgentExternalApproval[] }>(this.base));
      const fresh = (res?.data ?? []).map((a) => ({ ...a, state: 'pending' as const }));
      // keep just-decided cards visible for a moment so the person sees the outcome
      const settled = this.items().filter((a) => a.state && a.state !== 'pending' && a.state !== 'sending');
      this.items.set([...fresh, ...settled.filter((s) => !fresh.some((f) => f.id === s.id))]);
    } catch {
      // portal agent off, signed out or offline — the next tick tries again
    }
  }

  async decide(id: string, approve: boolean): Promise<void> {
    this.patch(id, { state: 'sending' });
    try {
      const res = await firstValueFrom(
        this.http.post<{ data: { status: AgentExternalApproval['state']; refresh?: string[] } }>(
          `${this.base}/${id}`, { approve },
        ),
      );
      const status = res?.data?.status ?? 'failed';
      this.patch(id, { state: status });
      if (status === 'executed' && res?.data?.refresh?.length) this.refresh.emit(res.data.refresh);
    } catch {
      this.patch(id, { state: 'failed' });
    }
    setTimeout(() => this.items.update((list) => list.filter((a) => a.id !== id)), SETTLED_SHOWN_MS);
  }

  private patch(id: string, change: Partial<AgentExternalApproval>): void {
    this.items.update((list) => list.map((a) => (a.id === id ? { ...a, ...change } : a)));
  }
}
